import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  X,
  CircleNotch,
  ArrowRight,
  ArrowLeft,
  InstagramLogo,
  Check,
} from "@phosphor-icons/react";
import { STICKERS, FRAMES } from "../../lib/shareStickers";

/**
 * Feature flag — Frames temporarily hidden ahead of Apple Build 33
 * resubmission so we can focus on the App Review fix without surfacing
 * in-progress UI. Flip back to `true` after approval to restore the
 * Frames picker, gallery, and PositionFrame views without any other
 * code changes.
 */
const FRAMES_ENABLED = false;

/**
 * Modal sheet shown when the user taps "Share" on a saved tip. Replaces
 * the older inline picker with three discrete views routed via local
 * `view` state:
 *   - main      → Frame + Sticker summary buttons, hide-amounts toggle,
 *                 Instagram CTA, Skip / Share buttons
 *   - frames    → 2-col gallery of frame previews
 *   - stickers  → 3-col gallery of sticker previews
 *   - position  → photo preview with draggable sticker
 *
 * The chosen frame id, sticker id, sticker position (normalized 0-1
 * relative to the photo box) and hideAmounts flag are passed to
 * `onShare()` so the parent can call renderTipCardBlob() with the right
 * options.
 */
export default function ShareStickerModal({ entry, onClose, onShare, sharing }) {
  const [view, setView] = useState("main");
  const [selectedFrame, setSelectedFrame] = useState(null);
  const [selectedSticker, setSelectedSticker] = useState(null);
  // Normalized 0-1 position of the sticker center relative to the photo
  // box. Default = top-right area (matches the legacy auto-anchor).
  const [stickerPos, setStickerPos] = useState({ x: 0.78, y: 0.18 });
  // Sticker scale factor — 1.0 = default ~32% of photo width. Pinched
  // outward grows, pinched inward shrinks (clamped 0.4 – 2.5).
  const [stickerScale, setStickerScale] = useState(1);
  // Frame scale + offset (normalized -0.4..0.4 to photo size). 1.0 = the
  // frame's painted edges match the photo edges (PNGs are pre-trimmed
  // so artwork reaches the PNG borders). User pinches to enlarge or
  // shrink from there if they want different coverage.
  const [frameScale, setFrameScale] = useState(1);
  const [frameOffset, setFrameOffset] = useState({ x: 0, y: 0 });
  const [hideAmounts, setHideAmounts] = useState(true);
  // Photo's natural aspect ratio (width/height). Loaded once on mount
  // so both the PositionFrame and PositionSticker views render the
  // photo at the SAME aspect the canvas renderer uses — i.e. WYSIWYG
  // matches the final share PNG. Default 4/3 until the image loads.
  const [photoAspect, setPhotoAspect] = useState(4 / 3);
  useEffect(() => {
    if (!entry?.food_photo_base64) return;
    const src = entry.food_photo_base64.startsWith("data:")
      ? entry.food_photo_base64
      : `data:image/jpeg;base64,${entry.food_photo_base64}`;
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth && img.naturalHeight) {
        setPhotoAspect(img.naturalWidth / img.naturalHeight);
      }
    };
    img.src = src;
  }, [entry?.food_photo_base64]);

  const frameMeta = FRAMES.find((f) => f.id === selectedFrame);
  const stickerMeta = STICKERS.find((s) => s.id === selectedSticker);

  const handleShare = (extra = {}) =>
    onShare(selectedSticker, {
      hideAmounts,
      frame: selectedFrame,
      frameTransform: selectedFrame
        ? { scale: frameScale, offsetX: frameOffset.x, offsetY: frameOffset.y }
        : null,
      stickerPos: selectedSticker
        ? { ...stickerPos, scale: stickerScale }
        : null,
      ...extra,
    });

  return (
    <div
      data-testid="share-sticker-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget && view === "main") onClose();
      }}
      className="fixed inset-0 z-50 bg-navy/40 flex items-start justify-center p-3 pt-6"
    >
      <div
        className="w-full max-w-md bg-white rounded-3xl shadow-float overflow-hidden flex flex-col max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {view === "main" && (
          <MainView
            onClose={onClose}
            frameMeta={frameMeta}
            stickerMeta={stickerMeta}
            onOpenFrames={() => setView("frames")}
            onOpenStickers={() => setView("stickers")}
            hideAmounts={hideAmounts}
            setHideAmounts={setHideAmounts}
            sharing={sharing}
            onShare={handleShare}
            onSkip={() => onShare(null, { hideAmounts, frame: null })}
          />
        )}
        {view === "frames" && FRAMES_ENABLED && (
          <FrameGallery
            selectedId={selectedFrame}
            onBack={() => setView("main")}
            onPick={(id) => {
              setSelectedFrame(id);
              if (id) {
                // Reset scale/offset so each new frame starts at the
                // default fit (PNG edges = artwork edges, so 1.0 hugs
                // the photo box exactly).
                setFrameScale(1);
                setFrameOffset({ x: 0, y: 0 });
                setView("position-frame");
              } else {
                setView("main");
              }
            }}
          />
        )}
        {view === "position-frame" && FRAMES_ENABLED && (
          <PositionFrame
            entry={entry}
            frame={frameMeta}
            photoAspect={photoAspect}
            scale={frameScale}
            offset={frameOffset}
            onChangeScale={setFrameScale}
            onChangeOffset={setFrameOffset}
            onBack={() => setView("frames")}
            onDone={() => setView("main")}
            onClear={() => {
              setSelectedFrame(null);
              setView("main");
            }}
          />
        )}
        {view === "stickers" && (
          <StickerGallery
            selectedId={selectedSticker}
            onBack={() => setView("main")}
            onPick={(id) => {
              setSelectedSticker(id);
              // Reset position & scale so each new sticker starts at the
              // default top-right anchor; user can drag/pinch from there.
              setStickerPos({ x: 0.78, y: 0.18 });
              setStickerScale(1);
              setView("position");
            }}
          />
        )}
        {view === "position" && (
          <PositionSticker
            entry={entry}
            sticker={stickerMeta}
            photoAspect={photoAspect}
            pos={stickerPos}
            scale={stickerScale}
            onChangePos={setStickerPos}
            onChangeScale={setStickerScale}
            onBack={() => setView("stickers")}
            onDone={() => setView("main")}
            onClear={() => {
              setSelectedSticker(null);
              setView("main");
            }}
          />
        )}
      </div>
    </div>
  );
}

/* ------------------------- Sub-views ------------------------- */

function MainView({
  onClose,
  frameMeta,
  stickerMeta,
  onOpenFrames,
  onOpenStickers,
  hideAmounts,
  setHideAmounts,
  sharing,
  onShare,
  onSkip,
}) {
  return (
    <div className="p-5 space-y-4 overflow-y-auto">
      <div className="flex items-center justify-between">
        <h3 className="font-jakarta font-extrabold text-navy text-lg">
          Personalize your share
        </h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="sticker-close-btn"
          className="w-9 h-9 rounded-full bg-bone/70 flex items-center justify-center"
        >
          <X size={18} weight="bold" color="#0E2C54" />
        </button>
      </div>

      <p className="text-navy-soft text-sm font-jakarta font-medium -mt-1">
        {FRAMES_ENABLED
          ? "Pick a frame and sticker (or skip both)."
          : "Pick a sticker (or skip)."}
      </p>

      {FRAMES_ENABLED && (
        <SummaryButton
          label="Frames"
          testid="open-frame-gallery-btn"
          selection={frameMeta}
          onClick={onOpenFrames}
          accent="sky"
        />
      )}
      <SummaryButton
        label="Stickers"
        testid="open-sticker-gallery-btn"
        selection={stickerMeta}
        onClick={onOpenStickers}
        accent="orange"
      />

      {/* Privacy toggle — switches the card into "postcard mode". */}
      <button
        type="button"
        role="switch"
        aria-checked={hideAmounts}
        data-testid="share-hide-amounts-toggle"
        onClick={() => setHideAmounts((v) => !v)}
        className={`w-full flex items-center justify-between gap-3 rounded-2xl border-2 px-4 py-3 transition-all active:scale-[0.98] ${
          hideAmounts
            ? "bg-sky-soft border-sky"
            : "bg-bone/40 border-transparent hover:bg-bone/70"
        }`}
      >
        <div className="flex flex-col items-start text-left min-w-0 flex-1">
          <span className="font-jakarta font-extrabold text-navy text-sm">
            {hideAmounts ? "Amounts hidden" : "Amounts visible"}
          </span>
          <span className="font-jakarta font-medium text-navy-soft text-xs">
            {hideAmounts
              ? "Postcard mode — bill, tip & total stay private"
              : "Bill, tip & total will show in shared image"}
          </span>
        </div>
        <span
          aria-hidden
          className={`relative inline-block w-12 h-7 rounded-full shrink-0 transition-colors ${
            hideAmounts ? "bg-sky" : "bg-navy/20"
          }`}
        >
          <span
            className={`absolute top-1 left-1 w-5 h-5 rounded-full shadow-soft transition-all ${
              hideAmounts ? "bg-green-500 translate-x-5" : "bg-red-500 translate-x-0"
            }`}
          />
        </span>
      </button>

      {hideAmounts && (
        <button
          type="button"
          data-testid="share-instagram-btn"
          disabled={sharing}
          onClick={() => onShare({ intent: "instagram" })}
          className="w-full rounded-full py-3 px-4 font-jakarta font-extrabold text-white flex items-center justify-center gap-2 shadow-soft active:scale-[0.98] transition-transform disabled:opacity-60"
          style={{
            background:
              "linear-gradient(95deg, #F58529 0%, #DD2A7B 40%, #8134AF 70%, #515BD4 100%)",
          }}
        >
          {sharing ? (
            <CircleNotch size={16} weight="bold" className="animate-spin" />
          ) : (
            <>
              <InstagramLogo size={18} weight="bold" />
              Post to Instagram Stories
            </>
          )}
        </button>
      )}

      <div className="flex gap-3 pt-1">
        <button
          type="button"
          onClick={onSkip}
          disabled={sharing}
          data-testid="share-skip-btn"
          className="flex-1 bg-white border border-navy/10 rounded-full py-3 font-jakarta font-bold text-navy disabled:opacity-60"
        >
          Skip
        </button>
        <button
          type="button"
          onClick={() => onShare()}
          disabled={sharing}
          data-testid="share-go-btn"
          className="flex-1 bg-orange text-white rounded-full py-3 font-jakarta font-extrabold disabled:opacity-60 flex items-center justify-center gap-1.5"
        >
          {sharing ? (
            <CircleNotch size={16} weight="bold" className="animate-spin" />
          ) : (
            <>
              Share <ArrowRight size={16} weight="bold" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function SummaryButton({ label, selection, onClick, accent, testid }) {
  const activeRing =
    accent === "sky" ? "ring-sky bg-sky-soft" : "ring-orange bg-orange-soft";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`w-full flex items-center justify-between gap-3 rounded-2xl border-2 border-transparent px-4 py-3 transition-all active:scale-[0.98] ${
        selection ? `${activeRing}` : "bg-bone/40 hover:bg-bone/70"
      }`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-2xl leading-none" aria-hidden>
          {selection?.emoji || "✨"}
        </span>
        <div className="flex flex-col items-start text-left min-w-0">
          <span className="font-jakarta font-extrabold text-navy text-sm">
            {label}
          </span>
          <span className="font-jakarta font-medium text-navy-soft text-xs">
            {selection ? selection.label : "Tap to browse"}
          </span>
        </div>
      </div>
      <ArrowRight size={18} weight="bold" color="#082140" />
    </button>
  );
}

/* ------------------------- Frame Gallery ------------------------- */

function FrameGallery({ selectedId, onBack, onPick }) {
  return (
    <div className="flex flex-col h-full overflow-hidden">
      <GalleryHeader title="Pick a frame" onBack={onBack} />
      <div
        data-testid="frame-gallery-grid"
        className="grid grid-cols-2 gap-3 p-4 overflow-y-auto"
      >
        {FRAMES.map((f) => {
          const isOn = selectedId === f.id;
          return (
            <button
              key={f.id}
              type="button"
              data-testid={`frame-gallery-option-${f.id}`}
              onClick={() => onPick(f.id)}
              className={`relative aspect-[5/4] rounded-2xl overflow-hidden border-2 bg-bone/40 transition-all active:scale-95 ${
                isOn ? "border-sky shadow-soft" : "border-transparent"
              }`}
            >
              {/* Faux "photo" underneath the frame so the user sees how
                  the frame wraps a real image. */}
              <div
                className="absolute"
                style={{
                  left: `${(f.inner?.x ?? 0.18) * 100}%`,
                  top: `${(f.inner?.y ?? 0.18) * 100}%`,
                  width: `${(f.inner?.w ?? 0.64) * 100}%`,
                  height: `${(f.inner?.h ?? 0.6) * 100}%`,
                  background:
                    "linear-gradient(135deg, #4A5D78 0%, #0E2C54 100%)",
                }}
              />
              {/* Frame overlay PNG (falls back to emoji + label if asset
                  is missing). Using backgroundImage so iOS Safari
                  doesn't paint its transparency-checker indicator
                  through the alpha pixels. */}
              <div
                aria-hidden
                className="absolute inset-0"
                style={{
                  backgroundImage: `url(${f.src})`,
                  backgroundSize: "100% 100%",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                }}
              />
              {/* Emoji fallback corner badge so the user always sees
                  *something* even before assets land. */}
              <span className="absolute top-1 left-2 text-xl drop-shadow" aria-hidden>
                {f.emoji}
              </span>
              <span className="absolute bottom-0 inset-x-0 bg-white/85 backdrop-blur-sm font-jakarta font-bold text-navy text-xs py-1 text-center">
                {f.label}
              </span>
              {isOn && (
                <span className="absolute top-2 right-2 w-7 h-7 bg-sky rounded-full flex items-center justify-center shadow-soft">
                  <Check size={14} weight="bold" color="white" />
                </span>
              )}
            </button>
          );
        })}
      </div>
      <ClearActionBar
        visible={!!selectedId}
        onClear={() => onPick(null)}
        accent="sky"
      />
    </div>
  );
}

/* ------------------------- Sticker Gallery ------------------------- */

function StickerGallery({ selectedId, onBack, onPick }) {
  return (
    <div className="flex flex-col h-full overflow-hidden">
      <GalleryHeader title="Pick a sticker" onBack={onBack} />
      <div
        data-testid="sticker-gallery-grid"
        className="grid grid-cols-3 gap-2.5 p-4 overflow-y-auto"
      >
        {STICKERS.map((s) => {
          const isOn = selectedId === s.id;
          const isImage = s.kind === "image";
          return (
            <button
              key={s.id}
              type="button"
              data-testid={`sticker-gallery-option-${s.id}`}
              onClick={() => onPick(s.id)}
              className={`relative aspect-square rounded-2xl border-2 bg-bone/40 flex flex-col items-center justify-center gap-1 p-2 transition-all active:scale-95 ${
                isOn ? "border-orange shadow-soft bg-orange-soft" : "border-transparent"
              }`}
            >
              {isImage ? (
                <>
                  <img
                    src={s.src}
                    alt=""
                    className="max-h-[60%] w-auto object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                  <span className="text-xl leading-none" aria-hidden>
                    {s.emoji}
                  </span>
                </>
              ) : (
                <span className="text-3xl leading-none" aria-hidden>
                  {s.emoji}
                </span>
              )}
              <span className="font-jakarta font-bold text-navy text-[10px] leading-tight text-center line-clamp-2">
                {s.label}
              </span>
            </button>
          );
        })}
      </div>
      <ClearActionBar
        visible={!!selectedId}
        onClear={() => onPick(null)}
        accent="orange"
      />
    </div>
  );
}

/* ------------------------- Sticker Positioning ------------------------- */

function PositionSticker({
  entry,
  sticker,
  photoAspect,
  pos,
  scale,
  onChangePos,
  onChangeScale,
  onBack,
  onDone,
  onClear,
}) {
  const photoRef = useRef(null);
  // Map of pointerId -> {x, y} in client coords. Drives both drag (1
  // pointer) and pinch-to-resize (2 pointers).
  const pointersRef = useRef(new Map());
  // Pinch baseline — captured when the second pointer goes down.
  const pinchRef = useRef({ startDistance: 0, startScale: 1 });
  const BASE_SIZE_PCT = 0.32; // sticker is ~32% of photo width at scale=1

  const photoSrc = entry?.food_photo_base64
    ? entry.food_photo_base64.startsWith("data:")
      ? entry.food_photo_base64
      : `data:image/jpeg;base64,${entry.food_photo_base64}`
    : null;

  const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

  const onPointerDown = useCallback(
    (e) => {
      if (!photoRef.current) return;
      e.preventDefault();
      e.target.setPointerCapture?.(e.pointerId);
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      // If we now have exactly 2 pointers, capture pinch baseline.
      if (pointersRef.current.size === 2) {
        const [a, b] = Array.from(pointersRef.current.values());
        pinchRef.current.startDistance = distance(a, b) || 1;
        pinchRef.current.startScale = scale;
      }
    },
    [scale]
  );

  const onPointerMove = useCallback(
    (e) => {
      if (!photoRef.current || !pointersRef.current.has(e.pointerId)) return;
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const rect = photoRef.current.getBoundingClientRect();

      if (pointersRef.current.size >= 2) {
        // Pinch — scale based on ratio of current to starting distance.
        const [a, b] = Array.from(pointersRef.current.values());
        const d = distance(a, b);
        const ratio = d / pinchRef.current.startDistance;
        const nextScale = Math.max(
          0.4,
          Math.min(2.5, pinchRef.current.startScale * ratio)
        );
        onChangeScale(nextScale);
        // Also re-center on the midpoint so the sticker follows the
        // pinch gesture naturally.
        const midX = (a.x + b.x) / 2;
        const midY = (a.y + b.y) / 2;
        onChangePos({
          x: Math.max(0.05, Math.min(0.95, (midX - rect.left) / rect.width)),
          y: Math.max(0.05, Math.min(0.95, (midY - rect.top) / rect.height)),
        });
      } else {
        // Drag — single pointer, move sticker to follow.
        onChangePos({
          x: Math.max(0.05, Math.min(0.95, (e.clientX - rect.left) / rect.width)),
          y: Math.max(0.05, Math.min(0.95, (e.clientY - rect.top) / rect.height)),
        });
      }
    },
    [onChangePos, onChangeScale]
  );

  const onPointerUp = useCallback((e) => {
    pointersRef.current.delete(e.pointerId);
    e.target.releasePointerCapture?.(e.pointerId);
  }, []);

  // Lock body scroll so multi-touch pinches stay on the sticker.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const sizePct = BASE_SIZE_PCT * scale;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <GalleryHeader title="Position sticker" onBack={onBack} />
      <div className="p-4 space-y-3 overflow-y-auto">
        <p className="font-jakarta text-xs text-navy-soft text-center">
          Drag to move · pinch with two fingers to resize
        </p>
        <div
          ref={photoRef}
          data-testid="sticker-position-canvas"
          className="relative w-full rounded-2xl overflow-hidden bg-navy/10 select-none touch-none"
          style={{
            aspectRatio: photoAspect || 4 / 3,
            backgroundImage: photoSrc ? `url(${photoSrc})` : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {!photoSrc && (
            <div className="absolute inset-0 flex items-center justify-center font-jakarta font-bold text-navy-soft text-sm">
              No photo on this tip
            </div>
          )}
          {sticker && (
            <div
              data-testid="sticker-position-handle"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              className="absolute cursor-grab active:cursor-grabbing flex items-center justify-center"
              style={{
                left: `${pos.x * 100}%`,
                top: `${pos.y * 100}%`,
                width: `${sizePct * 100}%`,
                aspectRatio: "1 / 1",
                transform: "translate(-50%, -50%)",
                filter: "drop-shadow(0 4px 14px rgba(0,0,0,0.45))",
                touchAction: "none",
                containerType: "size",
              }}
            >
              {/* Sticker renders directly on the photo — transparent
                  background. The emoji is the base layer (always
                  visible, even before the v2 PNG is uploaded). If the
                  image asset exists it overlays on top.

                  Emoji uses `cqmin` so it scales with the handle as the
                  user pinches in/out. */}
              <span
                className="leading-none pointer-events-none"
                style={{ fontSize: "85cqmin" }}
                aria-hidden
              >
                {sticker.emoji}
              </span>
              {sticker.kind === "image" && (
                <img
                  src={sticker.src}
                  alt=""
                  className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              )}
            </div>
          )}
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClear}
            data-testid="sticker-position-clear-btn"
            className="flex-1 bg-white border border-navy/10 rounded-full py-3 font-jakarta font-bold text-navy"
          >
            Remove
          </button>
          <button
            type="button"
            onClick={onDone}
            data-testid="sticker-position-done-btn"
            className="flex-1 bg-orange text-white rounded-full py-3 font-jakarta font-extrabold flex items-center justify-center gap-1.5"
          >
            <Check size={16} weight="bold" />
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------- Frame Positioning ------------------------- */

function PositionFrame({
  entry,
  frame,
  photoAspect,
  scale,
  offset,
  onChangeScale,
  onChangeOffset,
  onBack,
  onDone,
  onClear,
}) {
  const photoRef = useRef(null);
  // Map of pointerId -> {x, y}. 1 pointer drags the frame around the
  // photo; 2 pointers pinch-resize the frame.
  const pointersRef = useRef(new Map());
  const pinchRef = useRef({ startDistance: 0, startScale: 1 });
  const dragRef = useRef({ startX: 0, startY: 0, startOffsetX: 0, startOffsetY: 0 });

  const photoSrc = entry?.food_photo_base64
    ? entry.food_photo_base64.startsWith("data:")
      ? entry.food_photo_base64
      : `data:image/jpeg;base64,${entry.food_photo_base64}`
    : null;

  const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

  const onPointerDown = useCallback(
    (e) => {
      if (!photoRef.current) return;
      e.preventDefault();
      e.target.setPointerCapture?.(e.pointerId);
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointersRef.current.size === 2) {
        const [a, b] = Array.from(pointersRef.current.values());
        pinchRef.current.startDistance = distance(a, b) || 1;
        pinchRef.current.startScale = scale;
      } else if (pointersRef.current.size === 1) {
        dragRef.current = {
          startX: e.clientX,
          startY: e.clientY,
          startOffsetX: offset.x,
          startOffsetY: offset.y,
        };
      }
    },
    [scale, offset.x, offset.y]
  );

  const onPointerMove = useCallback(
    (e) => {
      if (!photoRef.current || !pointersRef.current.has(e.pointerId)) return;
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const rect = photoRef.current.getBoundingClientRect();

      if (pointersRef.current.size >= 2) {
        const [a, b] = Array.from(pointersRef.current.values());
        const d = distance(a, b);
        const ratio = d / pinchRef.current.startDistance;
        const nextScale = Math.max(
          0.6,
          Math.min(2.5, pinchRef.current.startScale * ratio)
        );
        onChangeScale(nextScale);
      } else {
        // Drag — translate frame relative to photo, normalized to
        // photo box width/height so the offset survives canvas scaling.
        const dx = (e.clientX - dragRef.current.startX) / rect.width;
        const dy = (e.clientY - dragRef.current.startY) / rect.height;
        onChangeOffset({
          x: Math.max(-0.4, Math.min(0.4, dragRef.current.startOffsetX + dx)),
          y: Math.max(-0.4, Math.min(0.4, dragRef.current.startOffsetY + dy)),
        });
      }
    },
    [onChangeScale, onChangeOffset]
  );

  const onPointerUp = useCallback((e) => {
    pointersRef.current.delete(e.pointerId);
    e.target.releasePointerCapture?.(e.pointerId);
    // If we dropped from 2 → 1 pointer, reset the drag baseline so
    // the remaining finger doesn't snap the frame by the accumulated
    // pinch delta on the next move event.
    if (pointersRef.current.size === 1) {
      const [remaining] = Array.from(pointersRef.current.values());
      dragRef.current = {
        startX: remaining.x,
        startY: remaining.y,
        startOffsetX: offset.x,
        startOffsetY: offset.y,
      };
    }
  }, [offset.x, offset.y]);

  // Lock body scroll so multi-touch pinches stay on the frame.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Frame overlay dimensions, expressed as % of the photo box. scale=1
  // means the frame's painted edge hugs the photo; >1 grows it outward.
  const frameW = scale * 100;
  const frameH = scale * 100;
  const frameLeft = 50 + offset.x * 100;
  const frameTop = 50 + offset.y * 100;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <GalleryHeader title="Adjust frame" onBack={onBack} />
      <div className="p-4 space-y-3 overflow-y-auto">
        <p className="font-jakarta text-xs text-navy-soft text-center">
          Drag to nudge · pinch with two fingers to resize
        </p>
        <div
          ref={photoRef}
          data-testid="frame-position-canvas"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          className="relative w-full rounded-2xl overflow-hidden bg-navy/10 select-none touch-none"
          style={{
            aspectRatio: photoAspect || 4 / 3,
            backgroundImage: photoSrc ? `url(${photoSrc})` : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {!photoSrc && (
            <div className="absolute inset-0 flex items-center justify-center font-jakarta font-bold text-navy-soft text-sm">
              No photo on this tip
            </div>
          )}
          {frame && (
            <div
              data-testid="frame-overlay"
              className="absolute pointer-events-none"
              style={{
                left: `${frameLeft}%`,
                top: `${frameTop}%`,
                width: `${frameW}%`,
                height: `${frameH}%`,
                transform: "translate(-50%, -50%)",
                backgroundImage: `url(${frame.src})`,
                backgroundSize: "100% 100%",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
              }}
            />
          )}
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClear}
            data-testid="frame-position-clear-btn"
            className="flex-1 bg-white border border-navy/10 rounded-full py-3 font-jakarta font-bold text-navy"
          >
            Remove
          </button>
          <button
            type="button"
            onClick={onDone}
            data-testid="frame-position-done-btn"
            className="flex-1 bg-orange text-white rounded-full py-3 font-jakarta font-extrabold flex items-center justify-center gap-1.5"
          >
            <Check size={16} weight="bold" />
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------- Shared bits ------------------------- */

function GalleryHeader({ title, onBack }) {
  return (
    <div className="flex items-center justify-between p-4 border-b border-navy/5 shrink-0">
      <button
        type="button"
        onClick={onBack}
        data-testid="gallery-back-btn"
        className="w-9 h-9 rounded-full bg-bone/70 flex items-center justify-center"
        aria-label="Back"
      >
        <ArrowLeft size={18} weight="bold" color="#0E2C54" />
      </button>
      <h3 className="font-jakarta font-extrabold text-navy text-lg">
        {title}
      </h3>
      <span className="w-9 h-9" />
    </div>
  );
}

function ClearActionBar({ visible, onClear, accent }) {
  if (!visible) return null;
  const fg = accent === "sky" ? "text-sky" : "text-orange";
  return (
    <div className="border-t border-navy/5 p-3 shrink-0">
      <button
        type="button"
        onClick={onClear}
        data-testid="gallery-clear-btn"
        className={`w-full font-jakarta font-bold ${fg} text-sm py-2`}
      >
        Clear selection
      </button>
    </div>
  );
}
